import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'react-toastify';
import PublicSEO from '../../components/public/PublicSEO';
import './AuthPublic.css';

const MIN_PASSWORD_LENGTH = 6;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(!!token);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError('Link non valido: manca il token. Richiedi un nuovo link dalla pagina Password dimenticata.');
      setValidating(false);
      setTokenValid(false);
      return undefined;
    }
    setValidating(true);
    setError('');
    api
      .get('/auth/reset-password/validate', { params: { token } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setError(res.data?.message || 'Link non valido o scaduto.');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setTokenValid(false);
        setError(getApiErrorMessage(err, 'Impossibile verificare il link. Richiedi un nuovo link.'));
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token || !tokenValid) return;
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`La password deve essere di almeno ${MIN_PASSWORD_LENGTH} caratteri.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Le due password non coincidono.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword });
      setSuccess(true);
      setTokenValid(false);
      toast.success('Password aggiornata. Puoi accedere con la nuova password.');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setTokenValid(false);
      setError(getApiErrorMessage(err, 'Errore durante l\'aggiornamento. Riprova o richiedi un nuovo link.'));
    } finally {
      setLoading(false);
    }
  };

  const showForm = !success && !validating && tokenValid;
  const showInvalid = !success && !validating && !tokenValid;

  return (
    <div className="vc-auth">
      <PublicSEO
        title="Imposta nuova password"
        description="Imposta la nuova password per il tuo account Virtual Clinic."
        canonicalPath="/reset-password"
        noIndex
      />
      <div className="vc-auth__card">
        <h1 className="vc-auth__title">Imposta nuova password</h1>
        <p className="vc-auth__lead">
          Almeno {MIN_PASSWORD_LENGTH} caratteri, poi conferma.
        </p>

        {validating && <p className="vc-auth__lead" role="status">Verifica del link in corso…</p>}

        {success && (
          <div className="vc-auth__error" style={{ background: '#e3f1ee', color: '#1f5f55' }} role="status">
            Password aggiornata. Stai per essere reindirizzato al login…
          </div>
        )}

        {showInvalid && (
          <div className="vc-auth__error" role="alert">
            {error || 'Link non valido o scaduto.'}
            <p style={{ margin: '0.65rem 0 0' }}>
              <Link to="/forgot-password" className="vc-auth__link">Richiedi un nuovo link</Link>
              {' · '}
              <Link to="/login" className="vc-auth__link">Vai al login</Link>
            </p>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSubmit}>
            {error && <div className="vc-auth__error" role="alert">{error}</div>}
            <div className="vc-auth__field">
              <label htmlFor="vc-reset-pw">Nuova password</label>
              <div className="vc-auth__input-wrap">
                <span className="vc-auth__icon" aria-hidden="true"><Icon icon="solar:lock-password-outline" /></span>
                <input
                  id="vc-reset-pw"
                  type={showPassword ? 'text' : 'password'}
                  className="vc-auth__input"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                  style={{ paddingRight: '3.25rem' }}
                />
                <button type="button" className="vc-auth__toggle-pw" onClick={() => setShowPassword((v) => !v)}>
                  {showPassword ? 'Nascondi' : 'Mostra'}
                </button>
              </div>
            </div>
            <div className="vc-auth__field">
              <label htmlFor="vc-reset-pw2">Conferma password</label>
              <div className="vc-auth__input-wrap">
                <span className="vc-auth__icon" aria-hidden="true"><Icon icon="solar:lock-password-outline" /></span>
                <input
                  id="vc-reset-pw2"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="vc-auth__input"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                  style={{ paddingRight: '3.25rem' }}
                />
                <button type="button" className="vc-auth__toggle-pw" onClick={() => setShowConfirmPassword((v) => !v)}>
                  {showConfirmPassword ? 'Nascondi' : 'Mostra'}
                </button>
              </div>
            </div>
            <button type="submit" className="vc-btn vc-btn-primary vc-auth__submit" disabled={loading}>
              {loading ? 'Aggiornamento…' : 'Imposta password'}
            </button>
          </form>
        )}

        <p className="vc-auth__footer">
          <Link to="/login" className="vc-auth__link">Torna al login</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
